import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraEntityMatchConfirm, SpecraSpinner, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { QUEUED_POLL_MAX_MS, useDecideIdentifierSuggestion, useIdentifierStatus, useSuggestIdentifiers } from './useIdentifierSuggestion';
const dayMonth = (d)=>d.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short'
    });
function useStillQueued(queued) {
    const [late, setLate] = useState(false);
    useEffect(()=>{
        if (!queued) {
            setLate(false);
            return;
        }
        const t = setTimeout(()=>setLate(true), QUEUED_POLL_MAX_MS);
        return ()=>clearTimeout(t);
    }, [
        queued
    ]);
    return late;
}
function Line({ children, testid }) {
    return /*#__PURE__*/ _jsx(SpecraText, {
        "data-testid": testid ?? Testids.registryIdentifiers.status,
        size: "meta",
        tone: "muted",
        children: children
    });
}
export function IdentifierSuggestionPanel({ kind, registryId, registryName, canWrite, density = 'inline' }) {
    const noun = kind === 'COMPANY' ? 'UEI' : 'contract number';
    const status = useIdentifierStatus(kind, registryId);
    const suggest = useSuggestIdentifiers(kind, registryId);
    const decide = useDecideIdentifierSuggestion(kind, registryId);
    const [closed, setClosed] = useState(false);
    const s = status.data;
    const code = s?.status ?? '';
    const stillQueued = useStillQueued(code === 'QUEUED');
    const [decideError, setDecideError] = useState('');
    if (code === 'HAS_IDENTIFIER') return null;
    const find = canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
        "data-testid": Testids.registryIdentifiers.findButton,
        disabled: suggest.isPending,
        onClick: ()=>suggest.mutate(false),
        size: "sm",
        variant: "secondary",
        children: [
            "Find ",
            noun
        ]
    }) : /*#__PURE__*/ _jsx(SpecraText, {
        size: "meta",
        tone: "muted",
        children: "—"
    });
    let body;
    const cand = s?.candidate;
    if (code === 'SUGGESTED' && cand && !closed) {
        const options = cand.publicOptions;
        const one = options.length === 1 ? options[0] : undefined;
        const send = (decision, chosenPublicKey)=>{
            setDecideError('');
            decide.mutate({
                candidateId: cand.id,
                expectedRevision: cand.revision,
                decision,
                chosenPublicKey
            }, {
                onError: (e)=>setDecideError(e instanceof Error ? e.message : 'Could not save that.')
            });
        };
        body = /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
            candidates: options.map((o)=>({
                    id: o.publicKey,
                    name: o.name,
                    context: o.context,
                    evidence: o.evidence,
                    details: o.details.map((d)=>({
                            label: d.label,
                            value: d.value
                        }))
                })),
            confirmLabel: `Yes, add ${noun}`,
            density: density,
            errorMessage: decideError || undefined,
            kind: kind,
            observedLabel: registryName,
            onConfirm: (key)=>send('CONFIRM', key),
            onDecline: ()=>send('REJECT'),
            onDefer: ()=>setClosed(true),
            question: one ? `Is ${registryName} the SAM.gov registration “${one.name}” (${one.context})?` : cand.question || undefined,
            readOnly: !canWrite,
            state: decide.isPending ? 'pending' : decideError ? 'error' : 'ask'
        });
    } else if (code === 'QUEUED') {
        body = stillQueued ? /*#__PURE__*/ _jsx(Line, {
            children: "Still queued — the result appears here when it's ready."
        }) : /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex items-center gap-[var(--space-1-5)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: "Looking this up in SAM.gov",
                    size: "sm"
                }),
                /*#__PURE__*/ _jsx(Line, {
                    children: "Looking this up in SAM.gov…"
                })
            ]
        });
    } else if (code === 'BUDGET_WAIT') {
        const at = s?.nextTryAt?.toDate();
        body = /*#__PURE__*/ _jsxs(Line, {
            children: [
                "Today's SAM.gov lookups are used up. We'll check after",
                ' ',
                at ? at.toLocaleString() : 'tomorrow',
                "."
            ]
        });
    } else if (code === 'NO_MATCH') {
        const at = s?.checkedAt?.toDate();
        body = /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex flex-wrap items-center gap-[var(--space-1-5)]",
            children: [
                /*#__PURE__*/ _jsxs(Line, {
                    children: [
                        "No SAM.gov registration matched ",
                        s?.queryLabel,
                        ".",
                        at ? ` Checked ${dayMonth(at)}.` : ''
                    ]
                }),
                canWrite && s?.canRecheck ? /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.registryIdentifiers.recheck,
                    disabled: suggest.isPending,
                    onClick: ()=>suggest.mutate(true),
                    size: "xs",
                    variant: "ghost",
                    children: "Check again"
                }) : null
            ]
        });
    } else if (code === 'NO_SOURCE') {
        body = /*#__PURE__*/ _jsx(Line, {
            children: "We can suggest a contract number once a holder company has a UEI and award data has loaded."
        });
    } else if (code === 'SOURCE_OFF') {
        body = /*#__PURE__*/ _jsx(Line, {
            children: "SAM.gov lookup isn't switched on here. Suggestions come from cached award data only."
        });
    } else if (code === 'DUPLICATE' && cand) {
        // REG-55's MergeRecordDialog is not on main yet: ask the question, no actions.
        const holder = cand.candidates[0];
        body = /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
            candidates: holder ? [
                {
                    id: holder.registryId,
                    name: holder.name
                }
            ] : [],
            density: density,
            kind: kind,
            observedLabel: registryName,
            onConfirm: ()=>undefined,
            question: `Are ${registryName} and ${holder?.name ?? 'another company'} the same company?`,
            readOnly: true
        });
    } else {
        body = find;
    }
    return /*#__PURE__*/ _jsx("div", {
        "data-status": code || 'none',
        "data-testid": Testids.registryIdentifiers.panel,
        children: body
    });
}
